import React, { Fragment } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRightIcon, ArrowRightIcon, FileBadgeIcon, CreditCardIcon, BellIcon, MapPinIcon } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
import { trackEvent } from '../../utils/analytics';
import { ANIMALS_CTA_IMAGE, animalsCtaCopy, ctaJourney, type Bi } from '../../data/animalsFinalCta';
import { AnimalsCtaLaptop } from './AnimalsCtaLaptop';
const EASE = [0.23, 1, 0.32, 1] as const;
const JOURNEY_ICONS = {
  register: FileBadgeIcon,
  pay: CreditCardIcon,
  remind: BellIcon,
  find: MapPinIcon
} as const;
const PHOTO_MASK = {
  WebkitMaskImage: 'linear-gradient(to right, transparent 0%, #000 24%)',
  maskImage: 'linear-gradient(to right, transparent 0%, #000 24%)'
};
export function AnimalsFinalCTA() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  const pick = (v: Bi) => isFR ? v.fr : v.en;
  const c = animalsCtaCopy;
  const reveal = {
    initial: {
      opacity: 0,
      y: 12
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true,
      margin: '-80px'
    }
  };
  const message = <>
      <p className="font-sans text-[13px] font-bold tracking-[0.3em] uppercase text-[#4F46E5]" data-id="element-210" data-mp="8t40td5xv85n">{pick(c.eyebrow)}</p>
      <h2 className="font-serif text-[2.6rem] sm:text-[3.4rem] 2xl:text-[4.4rem] leading-[1.04] tracking-tight mt-6" data-id="element-211" data-mp="3zsst8pe4lc5">
        <span className="block text-[#121A3A]" data-id="element-212" data-mp="3r6ldmmdixz6z">{pick(c.line1)}</span>
        <span className="block text-[#121A3A]" data-id="element-213" data-mp="3r7wh0l3254xk">{pick(c.line2)}</span>
        <span className="block text-[#4F46E5]" data-id="element-214" data-mp="3m6gv6qt22ue4">{pick(c.line3)}</span>
      </h2>
      <span className="block w-16 h-[3px] rounded-full bg-[#4F46E5] mt-8" aria-hidden="true" data-id="element-215" data-mp="3h7vtso22i4o3" />
      <div className="mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4" data-id="element-216" data-mp="3c9knuoew8eb5">
        <Link to="/request-demo" onClick={() => trackEvent('CTA Clicked', {
        location: 'animals_final',
        cta: 'request_demo'
      })} className="inline-flex items-center justify-center gap-2.5 rounded-xl bg-[#4F46E5] px-8 py-4 font-sans text-[16px] font-medium text-white whitespace-nowrap shadow-[0_14px_30px_-14px_rgba(79,70,229,0.7)] hover:bg-[#4338CA] transition-colors duration-150" data-id="element-217" data-mp="3cboydp7asbke">
          {pick(c.primary)}
          <ArrowUpRightIcon className="w-4 h-4" strokeWidth={2.2} aria-hidden="true" data-id="element-218" data-mp="2v11jn7k5siua" />
        </Link>
        <Link to="/request-demo" onClick={() => trackEvent('CTA Clicked', {
        location: 'animals_final',
        cta: 'talk_to_team'
      })} className="inline-flex items-center justify-center gap-2.5 rounded-xl border border-[#DCDBEF] bg-white px-8 py-4 font-sans text-[16px] font-medium text-[#121A3A] whitespace-nowrap hover:border-[#4F46E5] transition-colors duration-150" data-id="element-219" data-mp="3004nwpsbel17">
          {pick(c.secondary)}
          <ArrowRightIcon className="w-4 h-4" strokeWidth={2.2} aria-hidden="true" data-id="element-220" data-mp="3inrlo8wicdew" />
        </Link>
      </div>
    </>;
  return <div className="relative w-full" data-id="element-221" data-mp="27a5vqlitq7sb">
      {/* Desktop */}
      <div className="hidden xl:block relative max-w-[1760px] mx-auto min-h-[720px]" data-id="element-222" data-mp="27bqpwa187opn">
        <div className="absolute inset-y-0 right-0 w-[72%]" aria-hidden="true" data-id="element-223" data-mp="1zpbq8y8cui3a">
          <img src={ANIMALS_CTA_IMAGE} alt="" className="w-full h-full object-cover object-[55%_40%]" style={PHOTO_MASK} data-id="element-224" data-mp="1uqs4axtrh6ax" />
        </div>
        <span className="sr-only" data-id="element-225" data-mp="29mzymqyvvo3x">{pick(c.imageAlt)}</span>

        <motion.div {...reveal} transition={{
        duration: 0.3,
        delay: 0.1,
        ease: EASE
      }} className="absolute z-10 top-[150px] -right-[2%] w-[38%] max-w-[700px]" data-id="element-226" data-mp="24r14emqvjbvl">
          <AnimalsCtaLaptop isFR={isFR} data-id="element-227" data-mp="1c1c2m76cb7sx" />
        </motion.div>

        <svg className="absolute bottom-0 left-0 w-full h-[140px] pointer-events-none z-20" viewBox="0 0 1440 140" preserveAspectRatio="none" aria-hidden="true" data-id="element-228" data-mp="1c2vjnzztlmvr">
          <path d="M0 140 L0 70 C 500 150, 1000 120, 1440 10 L1440 140 Z" fill="#FFFFFF" data-id="element-229" data-mp="1lz8a8p02jkma" />
        </svg>

        <motion.div {...reveal} transition={{
        duration: 0.3,
        ease: EASE
      }} className="relative z-10 w-[40%] pl-6 2xl:pl-10 pt-24 pb-44" data-id="element-230" data-mp="y9470iqslmvt">
          {message}
        </motion.div>
      </div>

      {/* Tablet / mobile */}
      <div className="xl:hidden px-6 max-w-[1100px] mx-auto" data-id="element-231" data-mp="3sq4xsaye04h9">
        <motion.div {...reveal} transition={{
        duration: 0.3,
        ease: EASE
      }} data-id="element-232" data-mp="kj4xkdqjrtn">
          {message}
        </motion.div>
        <img src={ANIMALS_CTA_IMAGE} alt={pick(c.imageAlt)} className="mt-10 w-full aspect-[4/3] md:aspect-[16/9] rounded-[24px] object-cover object-[38%_40%]" data-id="element-233" data-mp="5j2t7rfsxfp6" />
        <div className="mt-6" data-id="element-234" data-mp="f8vsbsyrc2cj">
          <AnimalsCtaLaptop isFR={isFR} data-id="element-235" data-mp="f9nq9aftuc5z" />
        </div>
      </div>

      {/* Four-step journey */}
      <div className="relative z-30 bg-white xl:bg-transparent xl:-mt-12" data-id="element-236" data-mp="k773c70sgiwc">
        <ol className="max-w-[1500px] mx-auto px-6 pt-14 xl:pt-0 grid grid-cols-2 xl:grid-cols-[auto_1fr_auto_1fr_auto_1fr_auto] gap-y-10 gap-x-6 xl:gap-x-5 items-start" data-id="element-237" data-mp="obwy1sjpnrfo">
          {ctaJourney.map((s, i) => {
          const Icon = JOURNEY_ICONS[s.key];
          return <Fragment key={s.key} data-id="element-238" data-mp="2v09wxu597bmh" data-mpi={i}>
                <li className="flex flex-col items-center gap-4 xl:px-6" data-id="element-239" data-mp="2v1bkuz7dbslm">
                  <span className="w-[88px] h-[88px] rounded-full bg-[#F1F0FD] flex items-center justify-center" aria-hidden="true" data-id="element-240" data-mp="2nilqadt7u26d">
                    <Icon className="w-9 h-9 text-[#4F46E5]" strokeWidth={1.5} data-id="element-241" data-mp="2ioqzyrj3uoyb" />
                  </span>
                  <span className="font-sans text-[16px] font-bold uppercase tracking-[0.14em] text-[#1E2A6B]" data-id="element-242" data-mp="2dpo2pt9d9n0w">{pick(s.label)}</span>
                </li>
                {i < ctaJourney.length - 1 && <li className="hidden xl:flex items-center h-[88px]" aria-hidden="true" data-id="element-243" data-mp="36e26kpn4zdkd">
                    <span className="w-full h-[2px] rounded-full bg-[#E4E2FB]" data-id="element-244" data-mp="31f8o0iszswdc" />
                  </li>}
              </Fragment>;
        })}
        </ol>
      </div>
    </div>;
}